import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Trash2 } from "lucide-react";
import { employeeApi } from "../services/employeeApi";
import { formatApiErrorDetail } from "../lib/api";

const COLORS = ["bg-red-500", "bg-blue-500", "bg-emerald-500", "bg-amber-500", "bg-violet-500", "bg-teal-500", "bg-pink-500"];

function colorFor(name) {
  const code = (name || "").split("").reduce((sum, ch) => sum + ch.charCodeAt(0), 0);
  return COLORS[code % COLORS.length];
}

function initialsFor(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

export function EmployeeAvatar({ employee, editable = false, size = 72, onUpdated }) {
  const [hasPhoto, setHasPhoto] = useState(!!employee.photo_storage_path);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const fileInputRef = useRef(null);

  const dim = `${size}px`;

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      await employeeApi.uploadPhoto(employee.id, file);
      setHasPhoto(true);
      setVersion((v) => v + 1);
      toast.success("Photo updated");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await employeeApi.deletePhoto(employee.id);
      setHasPhoto(false);
      toast.success("Photo removed");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative shrink-0 group" style={{ width: dim, height: dim }} data-testid="employee-avatar">
      {hasPhoto ? (
        <img
          src={`${employeeApi.photoUrl(employee.id)}?v=${version}`}
          alt={employee.legal_name}
          className="rounded-full object-cover border border-gray-200"
          style={{ width: dim, height: dim }}
          onError={() => setHasPhoto(false)}
          data-testid="employee-avatar-image"
        />
      ) : (
        <div
          className={`rounded-full flex items-center justify-center text-white font-bold ${colorFor(employee.legal_name)}`}
          style={{ width: dim, height: dim, fontSize: size / 2.6 }}
          data-testid="employee-avatar-initials"
        >
          {initialsFor(employee.legal_name)}
        </div>
      )}
      {editable && (
        <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100">
          <button
            type="button"
            data-testid="employee-avatar-upload-btn"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
            className="p-1.5 rounded-full bg-white/90 text-gray-700 hover:bg-white disabled:opacity-50"
            title="Upload photo"
          >
            <Camera size={14} />
          </button>
          {hasPhoto && (
            <button
              type="button"
              data-testid="employee-avatar-remove-btn"
              disabled={busy}
              onClick={handleRemove}
              className="p-1.5 rounded-full bg-white/90 text-red-600 hover:bg-white disabled:opacity-50"
              title="Remove photo"
            >
              <Trash2 size={14} />
            </button>
          )}
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFileChange} data-testid="employee-avatar-file-input" />
        </div>
      )}
    </div>
  );
}
